import React, { useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { useSessionState, useScrollRestore, loadDraft, saveDraft, clearDraft } from '@/lib/sessionState';
import {
  History, Clock, FileBarChart2, Lock, CheckCircle2, AlertTriangle,
  Download, Loader2, ChevronRight, ShieldCheck, Pencil, X, Search,
  ExternalLink, RefreshCw,
} from 'lucide-react';

const EDIT_TARGETS = {
  admin_hub: { label: 'Admin Hub', route: '/tfs-admin' },
  tally: { label: 'Tally', route: '/contract-budgets' },
  rout: { label: 'ROUT', route: '/rout-admin' },
};


const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
const usd = (v) => (v === null || v === undefined ? '—'
  : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));

// Established Total Pay schedule: 1st–15th -> 20th; 16th–end & monthly -> 5th of next month.
const schedPayday = (cadence, year, month) => {
  if (!year || !month) return null;
  const mm = (n) => String(n).padStart(2, '0');
  const yy = (y) => String(y).slice(-2);
  if (cadence === 'first_half') return `${mm(month)}/20/${yy(year)}`;
  const nm = month === 12 ? 1 : month + 1;
  const ny = month === 12 ? year + 1 : year;
  return `${mm(nm)}/05/${yy(ny)}`;
};

// Any stored date -> MM/DD/YY (dates are stored as ISO 'YYYY-MM-DD'; no timezone shift).
const fmtMMDDYY = (s) => {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}/${m[3]}/${m[1].slice(-2)}`;
  return String(s);
};

// Payday for a list row: the established schedule rule, with the recorded actual date
// shown alongside only when it differs (always MM/DD/YY).
const rowPayday = (cadence, year, month, paymentDate) => {
  const sched = schedPayday(cadence, year, month);
  const actual = fmtMMDDYY(paymentDate);
  if (actual && actual !== sched) return `${sched} (actual ${actual})`;
  return sched || actual || '—';
};

const downloadFile = async (url, token, filename) => {
  const res = await axios.get(url, { ...auth(token), responseType: 'blob' });
  const href = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = href; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); window.URL.revokeObjectURL(href);
};

const LocationView = ({ data }) => {
  const [open, setOpen] = useState(null);
  const lv = data.location_view || [];
  const ls = data.location_summary || {};
  const budDisp = (e) => {
    if (e.budget_state === 'ok') return usd(e.budget);
    if (e.budget_state === 'shared') return `${usd(e.budget)} (shared)`;
    if (e.budget_state === 'unpaid') return `${usd(e.budget)} (${e.category === 'component' ? 'planned component' : 'no payroll'})`;
    if (e.budget_state === 'conflict') return 'Conflict — review';
    return 'Not mapped';
  };
  const budOk = (e) => ['ok', 'shared', 'unpaid'].includes(e.budget_state);
  return (
    <div className="mb-3 overflow-hidden rounded-lg border border-[#0E2A47]" data-testid="br-location-view">
      <div className="bg-[#0E2A47] px-3 py-2 text-sm font-bold text-white">By Service Location</div>
      <div className="px-3 py-1.5 text-[11px] text-slate-500">Each location combines all personnel paid there (including replacements); its applicable labor budget is counted once. Pay basis and rate stay in the By-Payee view.</div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead><tr className="bg-[#E7F0DE] text-left font-bold text-[#1A1A1A]">
            <th className="px-2 py-1">Service location</th><th className="px-2 py-1 text-right">Labor budget</th>
            <th className="px-2 py-1 text-right">Regular pay</th><th className="px-2 py-1 text-right">Additional pay</th>
            <th className="px-2 py-1 text-right">Total payout</th><th className="px-2 py-1 text-right">Variance</th><th className="px-2 py-1"></th>
          </tr></thead>
          <tbody>
            {lv.map((e, i) => (
              <React.Fragment key={i}>
                <tr className="border-t border-slate-100" data-testid="br-loc-row">
                  <td className="px-2 py-1 font-medium">{e.location}<div className="text-[9px] text-slate-400">{e.accounts.join(', ')}</div></td>
                  <td className={`px-2 py-1 text-right ${budOk(e) ? (e.budget_state === 'ok' ? '' : 'text-slate-500') : 'text-rose-500'}`} data-testid="br-loc-budget">{budDisp(e)}</td>
                  <td className="px-2 py-1 text-right">{usd(e.regular)}</td>
                  <td className="px-2 py-1 text-right">{usd(e.additional)}</td>
                  <td className="px-2 py-1 text-right font-medium">{usd(e.total)}</td>
                  <td className={`px-2 py-1 text-right ${e.variance === null ? 'text-slate-300' : e.variance < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{e.variance === null ? '—' : usd(e.variance)}</td>
                  <td className="px-2 py-1 text-right"><button onClick={() => setOpen(open === i ? null : i)} className="text-[10px] font-semibold text-[#0E2A47] underline" data-testid={`br-loc-detail-${i}`}>{open === i ? 'hide' : 'detail'}</button></td>
                </tr>
                {open === i && (
                  <tr className="bg-slate-50"><td colSpan={7} className="px-3 py-2">
                    <table className="w-full text-[10px]"><thead><tr className="text-left text-slate-500"><th className="pr-2">Payee #</th><th className="pr-2">Payee</th><th className="pr-2">Acct</th><th className="pr-2 text-right">Total</th></tr></thead>
                    <tbody>{e.rows.map((r, j) => <tr key={j}><td className="pr-2">{r.payee_number || '—'}</td><td className="pr-2">{r.payee}</td><td className="pr-2">{r.account_number}</td><td className="pr-2 text-right">{usd(r.total)}</td></tr>)}</tbody></table>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            <tr className="bg-[#E7F0DE] font-bold text-[#1A1A1A]">
              <td className="px-2 py-1">MATCHED-BUDGET TOTAL (mapped locations)</td>
              <td className="px-2 py-1 text-right">{usd(ls.matched_budget)}</td>
              <td className="px-2 py-1" colSpan={2}></td>
              <td className="px-2 py-1 text-right">{usd(ls.matched_actual)}</td>
              <td className={`px-2 py-1 text-right ${ls.matched_variance < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{usd(ls.matched_variance)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="px-3 py-1.5 text-[10px] text-slate-400">Variance shown only where a per-location budget and actual cover the same location, service component and period. '(shared)' = shared labor counted once at account level; '(no payroll)' = a budgeted branch with no payment matched this period; '(planned component)' = a budgeted service component (not a branch) not paid this period; 'Not mapped' = paid location with no per-location Tally labor budget. Budgeted-but-unpaid amounts are pending review, not confirmed savings.</div>
    </div>
  );
};

const WorkOrdersSection = ({ section, id, token, onRefresh }) => {
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!section || !(section.rows || []).length) return null;
  const doSearch = async (term) => {
    setQ(term);
    if (!term || term.trim().length < 2) { setResults([]); return; }
    try {
      const res = await axios.get(`${API}/payroll/work-orders/search?q=${encodeURIComponent(term.trim())}`, auth(token));
      setResults(res.data.results || []);
    } catch { setResults([]); }
  };
  const setWO = async (lineId, woNumber) => {
    setBusy(true); setErr(null);
    try {
      await axios.patch(`${API}/payroll/import/${id}/line/${lineId}`, { changes: { wo_number: woNumber } }, auth(token));
      setEditing(null); setQ(''); setResults([]);
      onRefresh && onRefresh();
    } catch (e) { setErr(e.response?.data?.detail || 'Could not update work order link'); }
    setBusy(false);
  };
  return (
    <div className="mb-3 overflow-hidden rounded-lg border border-[#0E2A47]" data-testid="br-work-orders">
      <div className="bg-[#0E2A47] px-3 py-2 text-sm font-bold text-white">{section.title}
        <span className="ml-2 text-[10px] font-normal opacity-70">{section.linked_count} linked · {section.unlinked_count} unlinked</span>
      </div>
      {err && <div className="bg-rose-50 px-3 py-1 text-[11px] text-rose-600" data-testid="wo-err">{err}</div>}
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead><tr className="bg-[#E7F0DE] text-left font-bold text-[#1A1A1A]">
            {section.columns.map((c, i) => <th key={c} className={`px-2 py-1 ${i === 7 ? 'text-right' : (i === 0 || i === 2 ? 'text-center' : '')}`}>{c}</th>)}
          </tr></thead>
          <tbody>
            {section.rows.map((r) => (
              <tr key={r.line_id} className="border-t border-slate-100 align-top" data-testid={`wo-row-${r.line_id}`}>
                <td className={`px-2 py-1 text-center ${!r.payee_number ? 'text-rose-500' : ''}`}>{r.payee_number || 'link needed'}</td>
                <td className="px-2 py-1">{r.payee}</td>
                <td className={`px-2 py-1 text-center ${(!r.cls || r.cls_conflict) ? 'text-rose-500' : ''}`} data-testid={`wo-cls-${r.line_id}`}>{r.cls || '— link needed'}{r.cls_conflict && <span className="block text-[9px]">source conflict</span>}</td>
                <td className={`px-2 py-1 ${!r.customer ? 'text-rose-500' : ''}`}>{r.customer || '— missing'}</td>
                <td className={`px-2 py-1 ${r.service_location_missing ? 'text-rose-500' : ''}`} data-testid={`wo-loc-${r.line_id}`}>{r.service_location || '— missing'}</td>
                <td className={`px-2 py-1 ${r.work_performed_missing ? 'text-rose-500' : ''}`}>{r.work_performed || '— missing'}</td>
                <td className="px-2 py-1">
                  {r.wo_linked
                    ? <span className="font-medium text-emerald-700" data-testid={`wo-num-${r.line_id}`}>{r.wo_number}{r.job_budget_ref ? <span className="block text-[9px] font-normal text-slate-400">{r.job_budget_ref}</span> : null}</span>
                    : <span className="text-rose-500" data-testid={`wo-num-${r.line_id}`}>{r.wo_number ? `${r.wo_number} (not in ROUT)` : 'not linked'}</span>}
                  <div><button onClick={() => { setEditing(editing === r.line_id ? null : r.line_id); setQ(''); setResults([]); setErr(null); }} className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#0E2A47] underline" data-testid={`wo-link-btn-${r.line_id}`}><Search size={10} />{r.wo_linked ? 'change / unlink' : 'link work order'}</button></div>
                  {editing === r.line_id && (
                    <div className="mt-1 w-56 rounded border border-slate-300 bg-white p-2 shadow" data-testid={`wo-link-panel-${r.line_id}`}>
                      <input autoFocus value={q} onChange={(e) => doSearch(e.target.value)} placeholder="Search WO # / customer / site" className="w-full rounded border border-slate-300 px-2 py-1 text-[11px]" data-testid={`wo-search-input-${r.line_id}`} />
                      <div className="mt-1 max-h-40 overflow-auto">
                        {results.map((w) => (
                          <button key={w.wo_number} disabled={busy} onClick={() => setWO(r.line_id, w.wo_number)} className="block w-full border-b border-slate-100 px-1 py-1 text-left text-[10px] hover:bg-slate-50" data-testid={`wo-result-${w.wo_number}`}>
                            <span className="font-semibold text-[#0E2A47]">{w.wo_number}</span> · {w.customer || '—'}
                            <span className="block text-slate-500">{w.service_location || '— no site'}{w.work_performed ? ` — ${w.work_performed}` : ''}</span>
                          </button>
                        ))}
                        {q.trim().length >= 2 && !results.length && <div className="px-1 py-1 text-[10px] text-slate-400">No matching ROUT work order</div>}
                      </div>
                      {r.wo_linked && <button disabled={busy} onClick={() => setWO(r.line_id, '')} className="mt-1 text-[10px] font-semibold text-rose-600 underline" data-testid={`wo-unlink-${r.line_id}`}>Unlink — keep as unmatched historical</button>}
                    </div>
                  )}
                </td>
                <td className="px-2 py-1 text-right font-medium">{usd(r.total)}</td>
              </tr>
            ))}
            <tr className="bg-[#E7F0DE] font-bold text-[#1A1A1A]">
              <td className="px-2 py-1" colSpan={7}>WORK ORDERS TOTAL</td>
              <td className="px-2 py-1 text-right" data-testid="wo-total">{usd(section.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="px-3 py-1.5 text-[10px] leading-tight text-slate-400">{section.note}</div>
    </div>
  );
};

const BudgetRegisterView = ({ data, id, token, onRefresh }) => {
  const [view, setView] = useState('payee');
  if (!data) return null;
  const s = data.summary;
  const vcls = (v) => (v === null || v === undefined ? 'text-slate-400' : v < 0 ? 'text-rose-600' : 'text-emerald-700');
  const COLS = data.columns || ['Payee #', 'Payee', 'Service location', 'Class', 'Pay basis', 'Rate', 'Hrs/Units', 'Regular pay', 'Additional pay', 'Payee total'];
  const Row = ({ r }) => {
    const num = r.payee_number;
    const numDisp = num ? String(num) : (['unmatched', 'suggested', null, undefined].includes(r.match_status) ? 'link needed' : '—');
    return (
      <tr className="border-t border-slate-100">
        <td className={`px-2 py-1 text-center ${!num && r.match_status !== 'matched' ? 'text-rose-500' : ''}`}>{numDisp}</td>
        <td className="px-2 py-1">{r.payee}{r.calc_detail ? <span title={r.calc_detail} className="ml-1 cursor-help text-[#0E2A47]" data-testid="br-calc-detail">ⓘ</span> : ''}{r.match_status === 'unmatched' ? <span className="text-rose-500"> (not linked)</span> : ''}{(r.flags || []).includes('possible_cross_sheet_duplicate') ? <span className="text-amber-600"> (dup?)</span> : ''}</td>
        <td className="px-2 py-1">{r.service_location || '—'}</td>
        <td className={`px-2 py-1 text-center ${r.cls_unknown ? 'text-rose-500' : ''}`}>{r.cls}</td>
        <td className={`px-2 py-1 ${r.basis_unknown ? 'text-rose-500' : ''}`}>{r.basis}</td>
        <td className="px-2 py-1 text-right">{usd(r.rate)}</td>
        <td className="px-2 py-1 text-right">{r.units_label}</td>
        <td className="px-2 py-1 text-right">{usd(r.regular)}</td>
        <td className="px-2 py-1 text-right">{usd(r.additional)}</td>
        <td className="px-2 py-1 text-right">{usd(r.total)}</td>
      </tr>
    );
  };
  const Band = ({ acc }) => {
    const rows = acc.rows || (acc.locations || []).flatMap((lc) => lc.rows);
    return (
    <div className="mb-3 overflow-hidden rounded-lg border border-[#0E2A47]" data-testid={`br-acct-${acc.account_number}`}>
      <div className="flex items-stretch justify-between bg-[#0E2A47] px-3 py-2 text-white">
        <div className="self-center text-sm font-bold">{acc.account_number !== 'WO' && acc.account_number !== '—' ? `${acc.account_number} — ` : ''}{acc.account_name}</div>
        <div className="flex gap-5 text-right text-[11px]">
          {acc.budget_state === 'ok'
            ? <div><div className="opacity-70">Budget</div><div className="font-bold">{usd(acc.budget)}</div></div>
            : <div className="max-w-[150px] self-center text-[10px] font-semibold text-amber-200">{acc.budget_note}</div>}
          <div><div className="opacity-70">Actual</div><div className="font-bold">{usd(acc.actual)}</div></div>
          <div><div className="opacity-70">Variance</div><div className={`font-bold ${acc.variance < 0 ? 'text-rose-300' : ''}`}>{acc.variance === null ? '—' : usd(acc.variance)}</div></div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead><tr className="bg-[#E7F0DE] text-left font-bold text-[#1A1A1A]">
            {COLS.map((c, i) => <th key={c} className={`px-2 py-1 ${i >= 5 ? 'text-right' : (i === 0 || i === 3 ? 'text-center' : '')}`}>{c}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => <Row key={i} r={r} />)}
            <tr className="bg-[#E7F0DE] font-bold text-[#1A1A1A]">
              <td className="px-2 py-1" colSpan={7}>{acc.account_number !== 'WO' && acc.account_number !== '—' ? `${acc.account_number} ACCOUNT TOTAL` : 'WORK ORDERS TOTAL'}</td>
              <td className="px-2 py-1 text-right">{usd(acc.totals.regular)}</td>
              <td className="px-2 py-1 text-right">{usd(acc.totals.additional)}</td>
              <td className="px-2 py-1 text-right">{usd(acc.totals.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
  };
  return (
    <div data-testid="br-view" style={{ fontFamily: "'Century Gothic', 'Jost', 'Trebuchet MS', sans-serif" }}>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-xl font-bold text-[#0E2A47]">{data.title}</div>
          <div className="text-xs text-slate-500">Pay period: {data.period_label}</div>
          <div className="text-sm font-bold text-[#0E2A47]">Payday: {data.payday_display || '—'} <span className="text-[11px] font-normal text-slate-400">({data.payday_note})</span></div>
          {data.governing_budget?.label && (
            <div className="mt-1.5 max-w-xl rounded-md border border-[#0E2A47]/20 bg-[#E7F0DE]/60 px-2.5 py-1.5" data-testid="br-governing">
              <div className="text-[11px] font-bold text-[#0E2A47]">{data.governing_budget.label}</div>
              {data.governing_budget.note && <div className="text-[9px] leading-tight text-slate-500">{data.governing_budget.note}</div>}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
        <div className="inline-flex overflow-hidden rounded-lg border border-[#0E2A47]" data-testid="br-view-toggle">
          <button onClick={() => setView('payee')} className={`px-3 py-1.5 text-xs font-semibold ${view === 'payee' ? 'bg-[#0E2A47] text-white' : 'text-[#0E2A47]'}`} data-testid="br-view-payee">By Payee</button>
          <button onClick={() => setView('location')} className={`px-3 py-1.5 text-xs font-semibold ${view === 'location' ? 'bg-[#0E2A47] text-white' : 'text-[#0E2A47]'}`} data-testid="br-view-location">By Service Location</button>
        </div>
        <div className="flex gap-2">
          {onRefresh && <button onClick={onRefresh} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50" data-testid="br-refresh"><RefreshCw size={13} />Refresh report</button>}
          <button onClick={() => downloadFile(`${API}/payroll/import/${id}/budget-register.pdf`, token, `${data.title}-${data.period_label}.pdf`)} className="inline-flex items-center gap-1 rounded-lg bg-[#0E2A47] px-3 py-1.5 text-xs font-semibold text-white" data-testid="br-pdf"><Download size={13} />Budget Register PDF</button>
          <button onClick={() => downloadFile(`${API}/payroll/import/${id}/budget-register.xlsx`, token, `${data.title}-${data.period_label}.xlsx`)} className="inline-flex items-center gap-1 rounded-lg border border-emerald-600 px-3 py-1.5 text-xs font-semibold text-emerald-700" data-testid="br-xlsx"><Download size={13} />Excel</button>
          <button onClick={() => downloadFile(`${API}/payroll/import/${id}/budget-register.docx`, token, `${data.title}-${data.period_label}.docx`)} className="inline-flex items-center gap-1 rounded-lg border border-[#0E2A47] px-3 py-1.5 text-xs font-semibold text-[#0E2A47]" data-testid="br-docx"><Download size={13} />Word</button>
        </div>
        </div>
      </div>
      {view === 'location' ? <LocationView data={data} /> : (<>
      {data.accounts.map((a) => <Band key={a.account_number} acc={a} />)}
      {data.work_orders_section && <WorkOrdersSection section={data.work_orders_section} id={id} token={token} onRefresh={onRefresh} />}
      </>)}
      {(() => {
        const cov = s.coverage || data.coverage || {};
        const trueVar = cov.matched_variance;
        const fullBudget = cov.governing_budget != null ? cov.governing_budget : s.budget;
        return (
          <div className="mb-2 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-[#0E2A47] px-4 py-3 text-white" data-testid="br-grand-total">
            <div className="text-base font-bold">GRAND TOTAL</div>
            <div className="flex flex-wrap gap-6 text-right text-xs">
              <div><div className="opacity-70">Total actual payroll</div><div className="font-bold" data-testid="br-gt-actual">{usd(s.actual)}</div></div>
              <div><div className="opacity-70">Full governing budget</div><div className="font-bold" data-testid="br-gt-budget">{usd(fullBudget)}{s.budget_partial ? ' (partial)' : ''}</div></div>
              <div><div className="opacity-70">Matched budget → actual</div><div className="font-bold" data-testid="br-gt-matched">{usd(cov.matched_budget)} → {usd(cov.matched_actual)}</div></div>
              <div><div className="opacity-70">True variance (matched)</div><div className={`font-bold ${trueVar < 0 ? 'text-rose-300' : 'text-emerald-200'}`} data-testid="br-gt-variance">{trueVar == null ? '—' : usd(trueVar)}</div><div className="mt-0.5 text-[9px] font-normal opacity-60">full budget − actual is NOT the variance — see coverage</div></div>
            </div>
          </div>
        );
      })()}
      {s.budget_partial && <div className="mb-3 rounded bg-amber-50 px-3 py-2 text-[11px] text-amber-800" data-testid="br-partial-warn">Budget total is PARTIAL — {s.accounts_without_budget} account(s) have no mapped Tally budget and are excluded from the budget column; their actuals are still included. See data issues below.</div>}
      {(s.coverage || data.coverage) && (() => {
        const cov = s.coverage || data.coverage;
        const Line = ({ label, value, emph, tone }) => (
          <div className={`flex items-center justify-between gap-3 border-t border-slate-100 px-3 py-1 ${emph ? 'bg-slate-50' : ''}`}>
            <span className={`${emph ? 'font-bold text-[#0E2A47]' : 'text-slate-600'}`}>{label}</span>
            <span className={`tabular-nums ${emph ? 'font-bold' : ''} ${tone || 'text-[#1A1A1A]'}`}>{value}</span>
          </div>
        );
        return (
          <div className="mb-3 overflow-hidden rounded-lg border border-[#0E2A47]" data-testid="br-coverage">
            <div className="bg-[#0E2A47] px-3 py-2 text-sm font-bold text-white">Budget coverage — full governing budget vs matched (like-for-like)</div>
            <div className="text-[11px]">
              <Line label="Governing Tally budget (all current budget lines)" value={usd(cov.governing_budget)} />
              <Line label="Actual payout this period (counted once)" value={usd(cov.actual_total)} />
              <Line label="Matched like-for-like — budget → actual" value={`${usd(cov.matched_budget)} → ${usd(cov.matched_actual)}`} emph />
              <Line label="True over/under (matched variance only)" value={cov.matched_variance == null ? '—' : usd(cov.matched_variance)} emph tone={cov.matched_variance < 0 ? 'text-rose-600' : 'text-emerald-700'} />
              <Line label="Shared labor budget (counted once at account level)" value={usd(cov.shared_budget)} />
              <Line label="Budgeted branches, no January payroll (pending — not a saving)" value={usd(cov.unpaid_branch_budget)} tone="text-slate-500" />
              <Line label="Planned service components, not paid this period (pending — not a saving)" value={usd(cov.unpaid_component_budget)} tone="text-slate-500" />
              <Line label="Paid locations with no mapped Tally budget" value={usd(cov.unmatched_actual)} tone="text-rose-500" />
            </div>
            <div className="px-3 py-1.5 text-[10px] leading-tight text-slate-400">{cov.note}</div>
          </div>
        );
      })()}
      {data.data_issues?.length > 0 && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="br-issues">
          <div className="mb-2 text-sm font-bold text-amber-900">Data issues for your Tally review ({data.data_issues.length})</div>
          <table className="w-full text-[11px]">
            <thead><tr className="text-left text-amber-800"><th className="py-1 pr-2">Account</th><th className="py-1 pr-2">Tally reference</th><th className="py-1 pr-2">Issue</th><th className="py-1 pr-2">Needs correction</th><th className="py-1">Fix in</th></tr></thead>
            <tbody>{data.data_issues.map((x, i) => {
              const t = EDIT_TARGETS[x.edit_target] || null;
              const href = t ? (x.edit_target === 'tally' && x.edit_id ? `${t.route}?budget=${x.edit_id}` : t.route) : null;
              return (
              <tr key={i} className="border-t border-amber-200 align-top">
                <td className="py-1 pr-2 font-semibold text-amber-900">{x.account}</td>
                <td className="py-1 pr-2 text-amber-700">{x.tally_ref || '—'}</td>
                <td className="py-1 pr-2 text-amber-900">{x.issue}</td>
                <td className="py-1 pr-2 text-amber-800">{x.needs}</td>
                <td className="py-1 whitespace-nowrap">
                  {t ? (
                    <a href={href} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1 rounded border border-amber-400 bg-white px-2 py-0.5 font-semibold text-amber-800 hover:bg-amber-100"
                       data-testid={`br-issue-fix-${i}`}><ExternalLink size={11} />{t.label}</a>
                  ) : <span className="text-amber-700" data-testid={`br-issue-fix-${i}`}>Payment rows below</span>}
                </td>
              </tr>
            ); })}</tbody>
          </table>
          <div className="mt-2 text-[10px] leading-tight text-amber-700">Edit links open the owning module in a new tab — Admin Hub (personnel, customers, locations, assignments), Tally (governing budgets & allocations), ROUT (work orders). Your staged corrections here are preserved; use “Refresh report” after editing to recompute the pending review without changing recorded payouts.</div>
        </div>
      )}
      <div className="text-[10px] text-slate-400">{data.generated_note} · {data.font_note}</div>
    </div>
  );
};

const BASES = [['', '—'], ['hourly', 'Hourly'], ['daily', 'Daily'], ['piece_rate', 'Piece rate'], ['flat', 'Fixed payout'], ['salary', 'Salary']];
const CLASSES = [['', '—'], ['W-2', 'W-2'], ['W-9', 'W-9'], ['SC', 'SC']];
const BASIS_LABEL = { hourly: 'Hourly', daily: 'Daily', piece_rate: 'Piece rate', piece: 'Piece rate', flat: 'Fixed payout', fixed: 'Fixed payout', salary: 'Salary', additional: 'Additional', monthly: 'Fixed payout', work_order: 'Work order' };
const basisLabel = (b) => (b ? (BASIS_LABEL[String(b).toLowerCase()] || b) : '—');

const EditLineModal = ({ importId, line, token, onClose, onSaved }) => {
  const base = {
    service_location: line.resolved_service_location || line.service_location || '',
    classification: line.resolved_classification || line.classification || '',
    pay_basis: line.resolved_pay_basis || line.pay_basis || '',
    rate: line.resolved_rate ?? line.rate ?? '',
    units: line.resolved_units ?? line.units ?? '',
    gross: line.gross ?? '',
  };
  const [f, setF] = useState(() => loadDraft(`${importId}:${line.id}`) || { ...base, reason: '' });
  const [link, setLink] = useState(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setF((p) => { const n = { ...p, [k]: v }; saveDraft(`${importId}:${line.id}`, n); return n; });

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      try { const r = await axios.get(`${API}/payroll/import/reports/payee-search?q=${encodeURIComponent(q)}`, auth(token)); setResults(r.data || []); } catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, token]);

  const grossChanged = String(f.gross) !== String(line.gross ?? '');
  const newGross = Number(f.gross || 0);
  const delta = newGross - Number(line.gross || 0);

  const save = async () => {
    setBusy(true); setErr(null);
    const changes = {};
    ['service_location', 'classification', 'pay_basis'].forEach((k) => { if ((f[k] || '') !== (base[k] || '')) changes[k] = f[k]; });
    ['rate', 'units'].forEach((k) => {
      if (String(f[k] ?? '') !== String(base[k] ?? '')) changes[k] = f[k] === '' ? null : Number(f[k]);
    });
    if (grossChanged && f.gross !== '') changes.gross = Number(f.gross);
    const body = { changes, reason: f.reason };
    if (link) body.link = link.action === 'unlinked' ? { action: 'unlinked' } : { master_id: link.master_id, kind: link.kind, payee_type: link.payee_type };
    if (Object.keys(changes).length === 0 && !link) { setErr('No changes to save'); setBusy(false); return; }
    try {
      const r = await axios.patch(`${API}/payroll/import/${importId}/line/${line.id}`, body, auth(token));
      clearDraft(`${importId}:${line.id}`);
      onSaved(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="edit-line-modal">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-base font-bold text-[#0E2A47]">Edit / resolve payment row</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="edit-line-close"><X size={18} /></button>
        </div>
        <div className="mb-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
          <b className="text-[#0E2A47]">{line.payee_name}</b> · {line.account_label} {line.account_number ? `#${line.account_number}` : ''} · recorded {usd(line.gross)}
        </div>

        <div className="mb-3">
          <div className="mb-1 text-xs font-semibold text-slate-600">Payee link (resolve identity → applies to all this payee's rows)</div>
          <div className="relative">
            <Search size={13} className="absolute left-2 top-2.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search personnel / vendors (active + inactive)…" className="w-full rounded-lg border border-slate-300 py-1.5 pl-7 pr-2 text-sm" data-testid="edit-payee-search" />
          </div>
          {results.length > 0 && (
            <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-slate-200">
              {results.map((r) => (
                <button key={r.master_id} onClick={() => { setLink(r); setQ(r.name); setResults([]); }} className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-slate-50" data-testid="edit-payee-result">
                  <span>{r.name} <span className="text-xs text-slate-400">· {r.classification}</span></span>
                  <span className={`text-[10px] ${r.active ? 'text-emerald-600' : 'text-slate-400'}`}>{r.active ? 'active' : 'inactive'}</span>
                </button>
              ))}
            </div>
          )}
          {link && <div className="mt-1 text-xs text-emerald-700" data-testid="edit-link-chosen">Will link to: {link.name === undefined ? 'named (no master)' : link.name}</div>}
          <button onClick={() => { setLink({ action: 'unlinked' }); setQ(''); }} className="mt-1 text-[11px] text-slate-400 underline">Mark this row as a named payee (no master)</button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-slate-600">Service location
            <input value={f.service_location} onChange={(e) => set('service_location', e.target.value)} className="mt-0.5 w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" data-testid="edit-service-location" /></label>
          <label className="text-xs text-slate-600">Classification
            <select value={f.classification} onChange={(e) => set('classification', e.target.value)} className="mt-0.5 w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" data-testid="edit-classification">{CLASSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
          <label className="text-xs text-slate-600">Pay basis
            <select value={f.pay_basis} onChange={(e) => set('pay_basis', e.target.value)} className="mt-0.5 w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" data-testid="edit-pay-basis">{BASES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
          <label className="text-xs text-slate-600">Rate
            <input type="number" step="0.01" value={f.rate} onChange={(e) => set('rate', e.target.value)} className="mt-0.5 w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" data-testid="edit-rate" /></label>
          <label className="text-xs text-slate-600">Hours / Days / Units
            <input type="number" step="0.01" value={f.units} onChange={(e) => set('units', e.target.value)} className="mt-0.5 w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" data-testid="edit-units" /></label>
          <label className="text-xs text-slate-600">Recorded payout
            <input type="number" step="0.01" value={f.gross} onChange={(e) => set('gross', e.target.value)} className={`mt-0.5 w-full rounded-lg border px-2 py-1 text-sm ${grossChanged ? 'border-amber-400 bg-amber-50' : 'border-slate-300'}`} data-testid="edit-gross" /></label>
        </div>

        {grossChanged && (
          <div className="mt-3" data-testid="edit-gross-preview">
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">Payout change: {usd(line.gross)} → <b>{usd(newGross)}</b> ({delta >= 0 ? '+' : ''}{usd(delta)}). A reason is required.</div>
            <input value={f.reason} onChange={(e) => set('reason', e.target.value)} placeholder="Reason for changing the recorded payout…" className="mt-2 w-full rounded-lg border border-amber-300 px-2 py-1 text-sm" data-testid="edit-reason" />
          </div>
        )}

        {err && <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-600" data-testid="edit-line-error">{err}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-1.5 text-sm font-semibold text-slate-600">Cancel</button>
          <button onClick={save} disabled={busy || (grossChanged && !f.reason)} className="inline-flex items-center gap-1 rounded-lg bg-[#0E2A47] px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50" data-testid="edit-line-save">{busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}Save pending change</button>
        </div>
        <div className="mt-2 text-[10px] text-slate-400">Original imported values and source references are preserved. Recorded payouts never change unless you edit them with a reason.</div>
      </div>
    </div>
  );
};

const ImportDetail = ({ id, token, meta, onChanged }) => {
  const [d, setD] = useState(null);
  const [reg, setReg] = useState(null);
  const [breg, setBreg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null);
  const [savedMsg, setSavedMsg] = useState(null);
  const load = useCallback(async () => {
    const r = await axios.get(`${API}/payroll/import/${id}`, auth(token));
    setD(r.data);
    try {
      const rr = await axios.get(`${API}/payroll/import/${id}/register`, auth(token));
      setReg(rr.data?.register || null);
    } catch { setReg(null); }
    try {
      const br = await axios.get(`${API}/payroll/import/${id}/budget-register`, auth(token));
      setBreg(br.data || null);
    } catch { setBreg(null); }
  }, [id, token]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="p-6 text-sm text-slate-400"><Loader2 className="inline animate-spin" size={14} /> Loading…</div>;
  const imp = d.import;
  const pending = imp.status === 'pending_ga_approval';
  const act = async (fn) => { setBusy(true); setErr(null); try { await fn(); await load(); onChanged && onChanged(); } catch (e) { setErr(e?.response?.data?.detail || 'Action failed'); } finally { setBusy(false); } };

  return (
    <div data-testid="ph-detail">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${imp.locked ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
          {imp.locked ? <><Lock size={11} className="mr-1 inline" />Locked historical</> : <><Clock size={11} className="mr-1 inline" />Pending approval</>}
        </span>
        <span className="text-sm font-bold text-[#0E2A47]">{imp.cadence_label} · {imp.period?.label}</span>
        <span className="text-xs text-slate-400" data-testid="ph-detail-payday">Payday {schedPayday(imp.cadence, imp.service_month?.year, imp.service_month?.month) || '—'}{imp.payment_date ? ` (actual ${fmtMMDDYY(imp.payment_date)})` : ' (per schedule)'}</span>
      </div>
      <div className="mb-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
        <div><div className="text-[11px] uppercase text-slate-400">Original gross</div><div className="font-semibold">{usd(d.original_gross)}</div></div>
        <div><div className="text-[11px] uppercase text-slate-400">Approved adj.</div><div className="font-semibold">{usd(d.approved_adjustments)}</div></div>
        <div><div className="text-[11px] uppercase text-slate-400">Net</div><div className="font-semibold">{usd(d.net_gross)}</div></div>
        <div><div className="text-[11px] uppercase text-slate-400">Unresolved rows{d.unresolved_people != null && d.unresolved_people !== d.unresolved_payees ? ` · ${d.unresolved_people} people` : ''}</div><div className={`font-semibold ${d.unresolved_payees ? 'text-rose-600' : 'text-emerald-600'}`} data-testid="ph-detail-unresolved">{d.unresolved_payees}</div></div>
      </div>

      {pending && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-3">
          <button onClick={() => downloadFile(`${API}/payroll/import/reports/unmatched/download?fmt=csv&import_id=${id}`, token, 'unmatched-payees.csv')}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600" data-testid="ph-unmatched-csv"><Download size={13} />Unmatched CSV</button>
          {d.unresolved_payees > 0 && (
            <span className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-600" data-testid="ph-unresolved-note">
              {d.unresolved_payees} payment row(s){d.unresolved_people != null && d.unresolved_people !== d.unresolved_payees ? ` · ${d.unresolved_people} distinct people` : ''} unresolved — edit each row below to link it before finalizing</span>
          )}
          {meta?.can_finalize ? (
            <button disabled={busy} onClick={() => act(() => axios.post(`${API}/payroll/import/${id}/finalize`, {}, auth(token)))}
              className="inline-flex items-center gap-1 rounded-lg bg-[#0E2A47] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" data-testid="ph-finalize">
              <ShieldCheck size={13} />Finalize &amp; Lock (GA)</button>
          ) : (
            <span className="text-xs text-slate-400">Only a Global Administrator can finalize &amp; lock.</span>
          )}
        </div>
      )}
      {savedMsg && <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700" data-testid="ph-saved-msg">{savedMsg}</div>}
      {err && <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-600" data-testid="ph-detail-error">{err}</div>}

      {breg && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
          <BudgetRegisterView data={breg} id={id} token={token} onRefresh={load} />
        </div>
      )}

      {d.corrections?.length > 0 && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-white p-3">
          <div className="mb-1 text-sm font-semibold text-[#0E2A47]">Corrections &amp; addenda</div>
          {d.corrections.map((c) => (
            <div key={c.id} className="flex items-center justify-between border-t border-slate-100 py-1 text-xs first:border-0" data-testid="ph-correction">
              <span>{c.type === 'override' ? 'GA override' : 'Change order'} · {c.reason}</span>
              <span className={c.status === 'approved' ? 'text-emerald-600' : 'text-amber-600'}>{usd(c.total_delta)} · {c.status}</span>
            </div>
          ))}
        </div>
      )}

      {d.accounts.map((a) => (
        <div key={a.account_label} className="mb-2 rounded-xl border border-slate-200 bg-white" data-testid="ph-account">
          <div className="flex items-center justify-between px-3 py-2">
            <div className="text-sm font-semibold text-[#0E2A47]">{a.account_label} {a.account_number && <span className="text-slate-400">#{a.account_number}</span>} {a.customer_name && <span className="text-xs font-normal text-slate-400">→ {a.customer_name}</span>}</div>
            <div className="text-sm font-bold">{usd(a.gross)}</div>
          </div>
          <div className="overflow-x-auto border-t border-slate-100">
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50 text-left text-slate-500">
                <th className="px-3 py-1">Payee</th><th className="px-2 py-1">Location</th><th className="px-2 py-1">Class</th>
                <th className="px-2 py-1">Basis</th><th className="px-2 py-1 text-right">Rate</th><th className="px-2 py-1 text-right">Units</th>
                <th className="px-2 py-1">Link</th><th className="px-2 py-1 text-right">Payout</th>{pending && <th className="px-2 py-1 text-right">Edit</th>}</tr></thead>
              <tbody>
                {a.lines.map((ln) => (
                  <tr key={ln.id} className="border-t border-slate-50" data-testid="ph-line-row">
                    <td className="px-3 py-1">{ln.payee_name}{ln.edited && <span className="ml-1 rounded bg-indigo-100 px-1 text-[9px] font-semibold text-indigo-700" data-testid="ph-line-edited">edited</span>}</td>
                    <td className="px-2 py-1 text-slate-500">{ln.resolved_service_location || ln.service_location || '—'}</td>
                    <td className="px-2 py-1 text-slate-500">{ln.resolved_classification || ln.classification || '—'}</td>
                    <td className="px-2 py-1 text-slate-500">{basisLabel(ln.resolved_pay_basis || ln.pay_basis)}</td>
                    <td className="px-2 py-1 text-right text-slate-500">{(ln.resolved_rate ?? ln.rate) ?? '—'}</td>
                    <td className="px-2 py-1 text-right text-slate-500">{(ln.resolved_units ?? ln.units) ?? '—'}</td>
                    <td className="px-2 py-1">{ln.matched_master_id ? <span className="text-emerald-600">linked</span> : (ln.match_resolution === 'left_unlinked' ? <span className="text-slate-400">named</span> : <span className="text-rose-600">unresolved</span>)}{ln.calc_diff && <AlertTriangle size={11} className="ml-1 inline text-amber-500" />}</td>
                    <td className="px-2 py-1 text-right font-medium">{usd(ln.gross)}</td>
                    {pending && <td className="px-2 py-1 text-right"><button onClick={() => setEditing(ln)} className="inline-flex items-center gap-0.5 rounded border border-[#0E2A47] px-2 py-0.5 text-[11px] font-semibold text-[#0E2A47] hover:bg-[#0E2A47] hover:text-white" data-testid={`ph-edit-line-${ln.id}`}><Pencil size={10} />Edit</button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {editing && (
        <EditLineModal importId={id} line={editing} token={token}
          onClose={() => setEditing(null)}
          onSaved={(res) => {
            setEditing(null);
            setSavedMsg(`Saved. Payroll total now ${usd(res.payroll_total)}${res.unresolved_remaining ? ` · ${res.unresolved_remaining} payee(s) still unresolved` : ' · all identities resolved'}.`);
            load(); onChanged && onChanged();
          }} />
      )}

      {reg && (
        <div className="mt-4 space-y-3" data-testid="ph-register">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-bold text-[#0E2A47]">Register, reconciliation &amp; source exceptions</div>
            <div className="flex gap-2">
              <button onClick={() => downloadFile(`${API}/payroll/import/${id}/register.pdf`, token, `payroll-register-${imp.period?.label || id}.pdf`)}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-[#0E2A47]" data-testid="ph-register-pdf"><Download size={13} />Register PDF</button>
              <button onClick={() => downloadFile(`${API}/payroll/import/${id}/register.docx`, token, `payroll-register-${imp.period?.label || id}.docx`)}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-[#0E2A47]" data-testid="ph-register-docx"><Download size={13} />Register Word</button>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border-2 border-[#0E2A47] bg-white px-4 py-2">
            <span className="text-sm font-bold text-[#0E2A47]">PAYROLL TOTAL (provisional)</span>
            <span className="text-lg font-bold text-[#0E2A47]" data-testid="ph-register-total">{usd(reg.payroll_total)}</span>
          </div>

          {reg.cross_sheet_duplicates?.length > 0 && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="ph-register-duplicates">
              <div className="mb-1 text-sm font-semibold text-amber-900">Possible cross-sheet duplicates <span className="text-xs font-normal text-amber-700">(review — NOT merged; both still in the total)</span></div>
              {reg.cross_sheet_duplicates.map((x, i) => (
                <div key={i} className="flex items-center justify-between border-t border-amber-200 py-1 text-xs first:border-0">
                  <span className="text-amber-900">{x.account} · {x.account_payee}{x.account_description ? ` (${x.account_description})` : ''} <span className="mx-1 text-amber-500">↔</span> Work order · {x.work_order_payee}{x.work_order_location ? ` · ${x.work_order_location}` : ''}</span>
                  <span className="font-semibold text-amber-900">{usd(x.amount)}</span>
                </div>
              ))}
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="mb-1 text-sm font-semibold text-[#0E2A47]">Reconciliation <span className="text-xs font-normal text-slate-400">(Excel SUMMARY is informational only)</span></div>
            <table className="w-full text-xs"><thead><tr className="text-left text-slate-500"><th className="py-1">Account</th><th className="py-1 text-right">Register</th><th className="py-1 text-right">SUMMARY</th><th className="py-1 text-right">Variance</th></tr></thead>
              <tbody>{reg.reconciliation.map((r, i) => (<tr key={i} className="border-t border-slate-50"><td className="py-1">{r.account}</td><td className="py-1 text-right">{usd(r.register_subtotal)}</td><td className="py-1 text-right text-slate-400">{r.summary_actual == null ? '—' : usd(r.summary_actual)}</td><td className={`py-1 text-right ${r.variance ? 'font-semibold text-rose-600' : ''}`}>{r.variance == null ? '—' : usd(r.variance)}</td></tr>))}</tbody>
            </table>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="mb-1 text-sm font-semibold text-[#0E2A47]">Source exceptions &amp; exclusions</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
              <div className="flex justify-between"><span className="text-slate-500">Zero-dollar rows excluded</span><span className="font-semibold">{reg.exclusions?.zero_dollar_rows ?? 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Work-description payees</span><span className="font-semibold">{reg.flags_summary?.payee_is_description ?? 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Unresolved payment rows</span><span className={`font-semibold ${reg.flags_summary?.unresolved_payee ? 'text-rose-600' : ''}`} data-testid="ph-src-unresolved-rows">{reg.flags_summary?.unresolved_payee ?? 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Unresolved people</span><span className={`font-semibold ${reg.flags_summary?.unresolved_people ? 'text-rose-600' : ''}`} data-testid="ph-src-unresolved-people">{reg.flags_summary?.unresolved_people ?? 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Unresolved accounts</span><span className={`font-semibold ${reg.flags_summary?.account_unresolved ? 'text-rose-600' : ''}`}>{reg.flags_summary?.account_unresolved ?? 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Account name mismatch</span><span className={`font-semibold ${reg.flags_summary?.account_name_mismatch ? 'text-amber-600' : ''}`}>{reg.flags_summary?.account_name_mismatch ?? 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Cross-sheet duplicates</span><span className={`font-semibold ${reg.flags_summary?.possible_cross_sheet_duplicates ? 'text-amber-600' : ''}`}>{reg.flags_summary?.possible_cross_sheet_duplicates ?? 0}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Recorded≠rate×units</span><span className="font-semibold">{reg.flags_summary?.calc_diff ?? 0}</span></div>
            </div>
            {imp.payment_date == null && <div className="mt-2 rounded bg-slate-50 px-2 py-1 text-[11px] text-slate-500">Payday {schedPayday(imp.cadence, imp.service_month?.year, imp.service_month?.month)} per Total Pay schedule. An actual payment-date exception can be recorded separately.</div>}
          </div>
        </div>
      )}
    </div>
  );
};

const ReportsPanel = ({ token }) => {
  const [scope, setScope] = useState('month');
  const [basis, setBasis] = useState('service_period');
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(1);
  const [pidx, setPidx] = useState(1);
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  const qs = () => `scope=${scope}&basis=${basis}&year=${year}&month=${month}&period_index=${pidx}`;
  const run = async () => { setBusy(true); try { const r = await axios.get(`${API}/payroll/reports/preview?${qs()}`, auth(token)); setPrev(r.data); } finally { setBusy(false); } };
  return (
    <div data-testid="ph-reports">
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-500">Scope<select value={scope} onChange={(e) => setScope(e.target.value)} className="ml-1 rounded border border-slate-300 px-2 py-1 text-sm" data-testid="ph-rep-scope"><option value="month">Month</option><option value="quarter">Quarter</option><option value="semiannual">Semiannual</option><option value="annual">Annual</option></select></label>
        <label className="text-xs text-slate-500">Basis<select value={basis} onChange={(e) => setBasis(e.target.value)} className="ml-1 rounded border border-slate-300 px-2 py-1 text-sm" data-testid="ph-rep-basis"><option value="service_period">Service period</option><option value="payment_date">Payment date</option></select></label>
        <label className="text-xs text-slate-500">Year<input type="number" value={year} onChange={(e) => setYear(+e.target.value)} className="ml-1 w-20 rounded border border-slate-300 px-2 py-1 text-sm" /></label>
        {scope === 'month' && <label className="text-xs text-slate-500">Month<input type="number" min="1" max="12" value={month} onChange={(e) => setMonth(+e.target.value)} className="ml-1 w-16 rounded border border-slate-300 px-2 py-1 text-sm" /></label>}
        {(scope === 'quarter' || scope === 'semiannual') && <label className="text-xs text-slate-500">#<input type="number" min="1" value={pidx} onChange={(e) => setPidx(+e.target.value)} className="ml-1 w-14 rounded border border-slate-300 px-2 py-1 text-sm" /></label>}
        <button onClick={run} disabled={busy} className="rounded-lg bg-[#0E2A47] px-3 py-1.5 text-sm font-semibold text-white" data-testid="ph-rep-preview">{busy ? 'Loading…' : 'Preview'}</button>
      </div>
      {prev && (
        <div data-testid="ph-rep-result">
          <div className="mb-2 flex flex-wrap items-center gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm">
            <span className="font-bold text-[#0E2A47]">{usd(prev.overall_gross)}</span>
            <span className="text-slate-500">{prev.scope_label} · {prev.basis_label}</span>
            <span className="text-slate-400">{prev.run_count} run(s) · {prev.payee_count} payee(s)</span>
            <button onClick={() => downloadFile(`${API}/payroll/reports/download?fmt=pdf&${qs()}`, token, 'payroll-report.pdf')} className="ml-auto inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs font-semibold" data-testid="ph-rep-pdf"><Download size={12} />PDF</button>
            <button onClick={() => downloadFile(`${API}/payroll/reports/download?fmt=docx&${qs()}`, token, 'payroll-report.docx')} className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs font-semibold" data-testid="ph-rep-docx"><Download size={12} />Word</button>
          </div>
          {prev.sections.map((s) => (
            <div key={s.label} className="mb-3">
              <div className="mb-1 text-sm font-semibold text-[#0E2A47]">{s.label}</div>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-xs"><thead><tr className="bg-slate-50 text-left text-slate-500">{s.headers.map((h) => <th key={h} className="px-2 py-1">{h}</th>)}</tr></thead>
                  <tbody>{s.rows.slice(0, 60).map((row, i) => <tr key={i} className="border-t border-slate-50">{row.map((c, j) => <td key={j} className={`px-2 py-1 ${(s.right_cols || []).includes(j) ? 'text-right' : ''}`}>{c}</td>)}</tr>)}</tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PayrollHistory = ({ meta, initialTab }) => {
  const { token } = useContext(AuthContext);
  const instanceKey = initialTab === 'reports' ? 'reports' : 'history';
  const [tab, setTab] = useSessionState(`payroll_tab_${instanceKey}`, initialTab || 'history');
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState([]);
  const [sel, setSel] = useSessionState(`payroll_sel_${instanceKey}`, null);
  const [fy, setFy] = useSessionState(`payroll_fy_${instanceKey}`, '');
  const [fm, setFm] = useSessionState(`payroll_fm_${instanceKey}`, '');
  const [unresOpen, setUnresOpen] = useState(false);
  const [unresData, setUnresData] = useState(null);
  const [unresBusy, setUnresBusy] = useState(false);
  useScrollRestore('payroll', sel, items.length > 0 || pending.length > 0);

  const toggleUnres = useCallback(async () => {
    const next = !unresOpen;
    setUnresOpen(next);
    if (next) {
      setUnresBusy(true);
      const q = [];
      if (fy) q.push(`year=${fy}`);
      if (fm) q.push(`month=${fm}`);
      try {
        const r = await axios.get(`${API}/payroll/import/pending/summary${q.length ? `?${q.join('&')}` : ''}`, auth(token));
        setUnresData(r.data);
      } catch { setUnresData(null); }
      setUnresBusy(false);
    }
  }, [unresOpen, fy, fm, token]);

  const load = useCallback(async () => {
    const q = [];
    if (fy) q.push(`year=${fy}`);
    if (fm) q.push(`month=${fm}`);
    const [h, p] = await Promise.all([
      axios.get(`${API}/payroll/history${q.length ? `?${q.join('&')}` : ''}`, auth(token)),
      axios.get(`${API}/payroll/import/pending`, auth(token)).catch(() => ({ data: [] })),
    ]);
    setItems(h.data.items || []);
    setPending(p.data || []);
  }, [token, fy, fm]);
  useEffect(() => { load(); }, [load]);

  const filtP = pending.filter((p) => (!fy || String(p.service_month?.year) === String(fy)) && (!fm || String(p.service_month?.month) === String(fm)));
  const pendingRows = filtP.map((p) => ({ source: 'import', id: p.id, kind: 'Pending review', cadence: p.cadence, cadence_label: p.cadence_label, year: p.service_month?.year, month: p.service_month?.month, period_label: p.period?.label, payment_date: p.payment_date, gross: p.totals?.gross, unresolved: p.unresolved_total, locked: false }));
  const list = tab === 'pending' ? pendingRows : items;
  const provisionalTotal = pendingRows.reduce((s, r) => s + (r.gross || 0), 0);
  const provisionalUnresolved = pendingRows.reduce((s, r) => s + (r.unresolved || 0), 0);

  return (
    <div className="space-y-4" data-testid="payroll-history">
      <div className="flex gap-2">
        {[['history', 'History', History], ['pending', 'Pending review', Clock], ['reports', 'Reports', FileBarChart2]].map(([k, l, I]) => (
          <button key={k} onClick={() => { setTab(k); setSel(null); }} className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-semibold ${tab === k ? 'bg-[#0E2A47] text-white' : 'bg-slate-100 text-slate-600'}`} data-testid={`ph-tab-${k}`}><I size={14} />{l}{k === 'pending' && pending.length ? ` (${pending.length})` : ''}</button>
        ))}
      </div>

      {tab === 'reports' ? <ReportsPanel token={token} />
        : sel ? (
          <div>
            <button onClick={() => { setSel(null); load(); }} className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500" data-testid="ph-back"><ChevronRight size={15} className="rotate-180" />Back to list</button>
            <ImportDetail id={sel} token={token} meta={meta} onChanged={load} />
          </div>
        ) : (
          <div>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <input placeholder="Year" value={fy} onChange={(e) => setFy(e.target.value)} className="w-24 rounded border border-slate-300 px-2 py-1 text-sm" data-testid="ph-filter-year" />
              <input placeholder="Month" value={fm} onChange={(e) => setFm(e.target.value)} className="w-24 rounded border border-slate-300 px-2 py-1 text-sm" data-testid="ph-filter-month" />
              <button onClick={() => { setFy('2026'); setFm('1'); }} className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600" data-testid="ph-filter-jan2026">January 2026</button>
              {(fy || fm) && <button onClick={() => { setFy(''); setFm(''); }} className="rounded-lg px-2 py-1 text-xs text-slate-400" data-testid="ph-filter-clear">Clear</button>}
            </div>
            {tab === 'pending' && pendingRows.length > 0 && (
              <div className="mb-3 rounded-xl border-2 border-[#0E2A47] bg-white" data-testid="ph-pending-summary">
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div><div className="text-[11px] uppercase text-slate-400">Combined provisional total{(fy || fm) ? ' · filtered' : ''}</div><div className="text-lg font-bold text-[#0E2A47]" data-testid="ph-provisional-total">{usd(provisionalTotal)}</div></div>
                  <button type="button" onClick={toggleUnres} className="group text-left" data-testid="ph-unresolved-toggle">
                    <div className="flex items-center gap-1 text-[11px] uppercase text-slate-400">Unresolved amount <ChevronRight size={12} className={`transition-transform ${unresOpen ? 'rotate-90' : ''}`} /></div>
                    <div className={`text-lg font-bold underline-offset-2 group-hover:underline ${provisionalUnresolved ? 'text-rose-600' : 'text-emerald-600'}`} data-testid="ph-provisional-unresolved">{usd(provisionalUnresolved)}</div>
                    <div className="text-[10px] font-normal text-slate-400">unresolved identities — click to expand</div>
                  </button>
                  <div className="max-w-xs text-xs text-slate-400">Provisional — staged for review only. Excluded from official payroll &amp; YTD until a Global Administrator finalizes each run.</div>
                </div>
                {unresOpen && (
                  <div className="border-t border-slate-200 px-4 py-3" data-testid="ph-unresolved-panel">
                    <div className="mb-2 text-[11px] leading-tight text-slate-500">{unresData?.unresolved_note || 'Payment rows whose payee is not yet linked to a master record. These dollars ARE still in the provisional total (counted once). Budget-mapping and work-order exceptions are shown separately inside each run\u2019s report.'}</div>
                    {unresBusy ? <div className="py-2 text-sm text-slate-400"><Loader2 size={14} className="mr-1 inline animate-spin" />Loading breakdown…</div>
                      : !unresData || (unresData.unresolved_rows_total || 0) === 0 ? <div className="py-2 text-sm text-emerald-600">All payee identities are resolved.</div>
                      : (unresData.runs || []).filter((rn) => (rn.unresolved_rows || []).length > 0).map((rn) => (
                        <div key={rn.id} className="mb-3" data-testid={`ph-unresolved-run-${rn.id}`}>
                          <button type="button" onClick={() => setSel(rn.id)} className="mb-1 flex w-full items-center justify-between text-left text-sm font-semibold text-[#0E2A47] hover:underline">
                            <span>{rn.cadence_label || rn.cadence} · {rn.period?.label}</span>
                            <span className="text-rose-600">{usd(rn.unresolved_total)} · {rn.unresolved_count} row(s) · {rn.unresolved_people} people</span>
                          </button>
                          <div className="overflow-x-auto rounded-lg border border-slate-100">
                            <table className="w-full text-[11px]">
                              <thead><tr className="bg-slate-50 text-left text-slate-500"><th className="px-2 py-1">Payee (unresolved)</th><th className="px-2 py-1">Account</th><th className="px-2 py-1">Service location</th><th className="px-2 py-1">Type</th><th className="px-2 py-1 text-right">Amount</th></tr></thead>
                              <tbody>{rn.unresolved_rows.map((r, i) => (
                                <tr key={i} className="border-t border-slate-50">
                                  <td className="px-2 py-1 font-medium text-rose-600">{r.payee_name}</td>
                                  <td className="px-2 py-1">{r.account}</td>
                                  <td className="px-2 py-1">{r.service_location || '—'}</td>
                                  <td className="px-2 py-1">{r.kind === 'work_order' ? 'Work order' : (r.classification || 'Regular')}</td>
                                  <td className="px-2 py-1 text-right tabular-nums">{usd(r.gross)}</td>
                                </tr>
                              ))}</tbody>
                            </table>
                          </div>
                          <div className="mt-0.5 text-[10px] text-slate-400">Reason: payee not yet linked to a personnel/vendor master record. Open the run to link each row (recorded payouts are preserved).</div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
            {list.length === 0 ? <div className="rounded-lg border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-400" data-testid="ph-empty">No records.</div>
              : <div className="space-y-2">
                {list.map((it) => (
                  <button key={it.id} onClick={() => it.source === 'import' && setSel(it.id)} className="flex w-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left hover:border-[#0E2A47]" data-testid="ph-row">
                    <div>
                      <div className="flex items-center gap-2 text-sm font-semibold text-[#0E2A47]">
                        {it.locked ? <Lock size={13} className="text-emerald-600" /> : <Clock size={13} className="text-amber-500" />}
                        {it.cadence_label || it.cadence} · {it.period_label}
                      </div>
                      <div className="text-xs text-slate-400">{it.kind} · {String(it.month || '').padStart(2, '0')}/{it.year} · pay {rowPayday(it.cadence, it.year, it.month, it.payment_date)}</div>
                    </div>
                    <div className="text-sm font-bold text-[#0E2A47]">{usd(it.gross)}</div>
                  </button>
                ))}
              </div>}
          </div>
        )}
    </div>
  );
};

export default PayrollHistory;
